import {throttle} from 'lodash';
import {RefObject, useEffect, useRef} from 'react';
import {scrollPositionActions} from '@contentlab/stores/scrollPositionStore';

const THROTTLE_DELAY = 500;

/**
 * Custom hook to manage scroll position for a given key.
 * Usage:
 * const scrollRef = useScrollPosition('my-scroll-key');
 * return <div ref={scrollRef}>...</div>;
 *
 * OR
 *
 * If you have a specific ref to an element:
 * const myRef = useRef<HTMLDivElement>(null);
 * useScrollPosition('my-scroll-key', myRef);
 * return <div ref={myRef}>...</div>;
 * @param key
 * @param ref
 */
function useScrollPosition<T extends HTMLElement>(
  key: string,
  ref?: RefObject<T>
) {
  const localRef = useRef<T>(null);
  const elementRef = ref || localRef;

  useEffect(() => {
    const el = elementRef.current;
    if (!el) {
      return () => {};
    }

    // Restore scroll position on mount
    el.scrollTop = scrollPositionActions.getScrollPosition(key);

    const handleScroll = throttle(() => {
      scrollPositionActions.setScrollPosition(key, el.scrollTop);
    }, THROTTLE_DELAY);

    el.addEventListener('scroll', handleScroll);
    return () => {
      el.removeEventListener('scroll', handleScroll);
      handleScroll?.cancel?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, elementRef.current]);

  return elementRef;
}

export default useScrollPosition;
